import {
  Component,
  Input,
  OnInit,
  OnDestroy,
  OnChanges,
  SimpleChanges,
  inject,
} from '@angular/core';
import {
  IonContent,
  IonFooter,
  IonHeader,
  IonTitle,
  IonToolbar,
  IonButtons,
  IonRefresher,
  IonRefresherContent,
} from '@ionic/angular/standalone';
import { ThemeService } from '../../themes/theme.service';
import { getTheme } from '../../themes/theme.config';
import { Subscription } from 'rxjs';
import { CommonModule } from '@angular/common';
import { PwaUpdateService } from '../../services/pwa-update.service';
import { CrowdctrlBackButtonComponent } from '../back-button/back-button.component';

/**
 * Simplified page component for unauthenticated pages (login, unauthorized, etc.).
 * Does not include user profile button or other authenticated-only features.
 */
@Component({
  selector: 'crowdctrl-unauthenticated-page',
  imports: [
    CommonModule,
    CrowdctrlBackButtonComponent,
    IonContent,
    IonFooter,
    IonHeader,
    IonTitle,
    IonToolbar,
    IonButtons,
    IonRefresher,
    IonRefresherContent,
  ],
  templateUrl: './unauthenticated-page.component.html',
  styleUrl: './unauthenticated-page.component.css',
  standalone: true,
})
export class UnauthenticatedPageComponent implements OnInit, OnDestroy, OnChanges {
  /**
   * Title to display in the header toolbar
   */
  @Input() pageTitle: string = '';

  /**
   * Text to display in the footer.
   * If not provided, footer will be hidden.
   */
  @Input() footerText: string | null = null;

  /**
   * Whether to show the header. Defaults to true.
   */
  @Input() showHeader: boolean = true;

  /**
   * Whether to show the footer. Defaults to true if footerText is provided.
   */
  @Input() showFooter: boolean | null = null;

  /**
   * Additional CSS classes to apply to `ion-content` (Ionic utilities, e.g. `ion-padding`, `ion-margin`).
   * Default uses Ionic scroll inset; override per route when you need edge-to-edge lists.
   */
  @Input() contentClass: string = 'ion-padding';

  /**
   * When false, disables vertical scrolling on ion-content (e.g. fixed-viewport login).
   */
  @Input() contentScrollY: boolean = true;

  /**
   * When true, adds a full-height flex slot under the header. Prefer viewport min-height on page
   * content instead: ion-content flex chains are easy to collapse to zero with Ionic. Default
   * false.
   */
  @Input() fillContentSlot: boolean = false;

  /**
   * When true, projects `.unauth-main` into `ion-content` and `.unauth-sticky-footer` into
   * `ion-footer` (outside the scroll region). Use for login so legal/footer cannot overlap
   * vertically centered content in short viewports — idiomatic Ionic (header / content / footer).
   */
  @Input() stickyFooterOutsideScroll: boolean = false;

  /**
   * When true (and `showHeader` is false), renders a borderless `ion-header` before `ion-content`
   * and projects `.unauth-brand-header` into it. Login uses in-content branding instead so the
   * scroll body keeps vertical room in landscape.
   */
  @Input() showBrandHeader: boolean = false;

  /**
   * When true, the document uses the RMIT light palette while this page is shown; the user's
   * saved theme (and {@link ThemeService#currentTheme$}) is unchanged. Pair with login / about.
   */
  @Input() lockDocumentThemeToRmit: boolean = false;

  /**
   * Passed to `ion-back-button.defaultHref`: where to go when Ionic has no entry to pop in the
   * nav stack (see Ionic docs).
   */
  @Input() defaultHref: string | null = null;

  /**
   * When true, pull-to-refresh runs a PWA / service worker update check when
   * {@link PwaUpdateService#isEnabled} is true. Hidden when the app has no service worker.
   */
  @Input() enablePwaPullRefresh = false;

  /**
   * Optional `ion-back-button` icon override.
   */
  @Input() backButtonIcon?: string;

  /**
   * Optional toolbar color override.
   * If provided, this will override the theme-based toolbar color.
   * If not provided, the toolbar color will be based on the current theme.
   */
  @Input() toolbarColor?: string;

  /**
   * Computed toolbar color based on input or theme
   */
  computedToolbarColor: string = 'primary';

  get ionContentClass(): string {
    const base = (this.contentClass ?? '').trim();
    if (!this.fillContentSlot) {
      return base;
    }
    return base ? `${base} unauth-ion-content-fill` : 'unauth-ion-content-fill';
  }

  /**
   * Forms/Agent login use `login-page-ion-content` and need the projected stack to fill the scroll
   * column; the generic `.page-slot > :only-child { min-height: 0 }` collapses height and re-centres
   * the form in a short band at the top.
   */
  get isLoginPageContentSlot(): boolean {
    return /\blogin-page-ion-content\b/.test(this.contentClass ?? '');
  }

  private themeSubscription?: Subscription;
  private themeService = inject(ThemeService);
  readonly pwaUpdateService = inject(PwaUpdateService);
  private themeLockActive = false;

  get showPwaPullRefresher(): boolean {
    return this.enablePwaPullRefresh && this.pwaUpdateService.isEnabled;
  }

  onPwaPullRefresh(event: Event): void {
    const target = event.target as HTMLIonRefresherElement;
    void this.pwaUpdateService.checkForUpdates().finally(() => target.complete());
  }

  ngOnInit(): void {
    try {
      if (this.lockDocumentThemeToRmit) {
        this.themeService.beginDocumentThemeOverride('rmit-light');
        this.themeLockActive = true;
      }

      // Set initial toolbar color
      this.updateToolbarColor();

      // Subscribe to theme changes (only if toolbarColor is not explicitly set)
      try {
        this.themeSubscription = this.themeService.currentTheme$.subscribe(() => {
          try {
            if (!this.toolbarColor) {
              this.updateToolbarColor();
            }
          } catch (error) {
            console.error('[UnauthenticatedPageComponent] Error in theme subscription:', error);
          }
        });
      } catch (error) {
        console.error('[UnauthenticatedPageComponent] Error subscribing to theme:', error);
      }
    } catch (error) {
      console.error('[UnauthenticatedPageComponent] Critical error in ngOnInit:', error);
      // Ensure header still shows even if initialization fails
      this.showHeader = true;
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    // If toolbarColor input changes, update the computed color
    if (changes['toolbarColor']) {
      this.updateToolbarColor();
    }
  }

  ngOnDestroy(): void {
    if (this.themeLockActive) {
      this.themeService.endDocumentThemeOverride();
      this.themeLockActive = false;
    }
    if (this.themeSubscription) {
      this.themeSubscription.unsubscribe();
    }
  }

  private updateToolbarColor(): void {
    // If toolbarColor is explicitly set via input, use it
    if (this.toolbarColor) {
      this.computedToolbarColor = this.toolbarColor;
      return;
    }

    // Locked public pages use RMIT palette on the document; match toolbar contrast to that.
    const currentTheme = this.lockDocumentThemeToRmit
      ? 'rmit-light'
      : this.themeService.getCurrentTheme();
    if (currentTheme) {
      const theme = getTheme(currentTheme);
      if (theme) {
        // Use 'dark' color for dark themes, 'light' for light themes
        // This ensures proper contrast
        this.computedToolbarColor = theme.isDark ? 'dark' : 'light';
      }
    }
  }

  get shouldShowFooter(): boolean {
    if (this.showFooter !== null) {
      return this.showFooter;
    }
    return this.footerText !== null && this.footerText !== '';
  }
}
